import { useLocation } from "react-router-dom";

import { Accordion, AccordionItem } from "@/components/accordion/accordion";
import { Icon } from "@/components/icon/icon";
import ScrollArea from "@/components/scroll-area/scroll-area";
import { PublishedNavigationElement } from "@/lib/api";
import { useIsAuthenticated } from "@/features/shared/features/authentication";
import {
  NAVIGATION_ELEMENT_KIND,
  navigationElementKind,
} from "@/lib/navigation-element-kinds";

import NavigationChild from "./navigation-child";
import NavigationItem from "./navigation-item";
import NavigationPageItem from "./navigation-page-item";
import NavigationSubItems from "./navigation-subitems";
import NavigationWarningPageLink from "./navigation-warning-page-link";
import { NavigationWarningsPageLink } from "./navigation-warnings-link";
import styles from "./navigation.module.css";

interface NavigationElementsProps {
  navigationElements?: PublishedNavigationElement[];
}

export default function NavigationElements({
  navigationElements,
}: NavigationElementsProps) {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  return (
    <ScrollArea>
      {navigationElements?.map((navElement) => {
        const kind = navigationElementKind(navElement);
        if (kind === NAVIGATION_ELEMENT_KIND.publicWarningsPage) {
          return isAuthenticated ? (
            <NavigationWarningPageLink key={navElement.id} element={navElement} />
          ) : null;
        }
        if (kind === NAVIGATION_ELEMENT_KIND.maritimeWarningsPage) {
          return isAuthenticated ? (
            <NavigationWarningsPageLink key={navElement.id} element={navElement} />
          ) : null;
        }
        const isChildActive = navElement.children?.some(
          (child) => child.full_path && location.pathname === child.full_path,
        );

        const item = navElement.children ? (
          <Accordion
            key={navElement.id}
            collapsible
            defaultValue={isChildActive ? [navElement.id] : undefined}
          >
            <AccordionItem
              value={navElement.id}
              trigger={(context) => (
                <NavigationItem
                  icon={navElement.icon ?? "folder"}
                  active={isChildActive}
                  tooltip={<NavigationSubItems subitems={navElement.children} />}
                >
                  {navElement.title}
                  {context.expanded ? (
                    <Icon name="chevron-up" />
                  ) : (
                    <Icon name="chevron-down" />
                  )}
                </NavigationItem>
              )}
            >
              <div className={styles.navigationChildren}>
                {navElement.children?.map((childElement) =>
                  childElement.full_path ? (
                    <NavigationChild
                      key={childElement.id}
                      element={childElement}
                      active={childElement.full_path === location.pathname}
                    />
                  ) : undefined,
                )}
              </div>
            </AccordionItem>
          </Accordion>
        ) : navElement.full_path ? (
          <NavigationPageItem
            key={navElement.id}
            element={{ ...navElement, full_path: navElement.full_path }}
            active={location.pathname === navElement.full_path}
          />
        ) : null;

        return item;
      })}
    </ScrollArea>
  );
}
